import { Box, Card, Chip, CircularProgress, Container, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { HOST_API } from 'src/config-global';
import { useSettingsContext } from "src/components/settings";
import {
  CAMERA_VIDEO_STATUS_AVAILABLE,
  CAMERA_VIDEO_STATUS_FAILED,
  CAMERA_VIDEO_STATUS_PENDING,
  CAMERA_VIDEO_STATUS_QUEUED,
  Camera,
  CameraNotification,
  Device,
  EldVehicle,
} from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import {
  CameraMediaDialog,
  CameraMediaLoading,
  CameraMediaMessage,
} from "src/components/camera-media-dialog";
import Iconify from "src/components/iconify";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import { addDaysToDate, today } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";
import { STORAGE_KEY } from "src/auth/context/auth-provider";
import NoneFoundReport from "src/components/none-found-report";
import { VideoJS } from "src/pages/app/devices/video-js";
import { NoCamerasFound } from "../list";

type CameraWithDevice = Camera & {
  device?: Device
}

function formatEventTime(eventDateTime: string | undefined, timezone: string) {
  if (!eventDateTime) {
    return "";
  }
  const parsed = DateTime.fromISO(eventDateTime, { zone: "utc" });
  if (!parsed.isValid) {
    return eventDateTime;
  }
  return parsed.setZone(timezone).toFormat("MMM d, yyyy h:mm:ss a");
}

// Split the event timestamp into a bold time and a muted date so the row reads
// as an inbox entry rather than one long string.
function eventTimeParts(eventDateTime: string | undefined, timezone: string) {
  if (!eventDateTime) {
    return { time: "", date: "" };
  }
  const parsed = DateTime.fromISO(eventDateTime, { zone: "utc" });
  if (!parsed.isValid) {
    return { time: eventDateTime, date: "" };
  }
  const local = parsed.setZone(timezone);
  return { time: local.toFormat("h:mm a"), date: local.toFormat("MMM d, yyyy") };
}

// A compact two-line timestamp cell (bold time over a muted date), or a dash when
// the value is missing (e.g. a clip that hasn't uploaded yet).
function TimeCell({ value, timezone }: { value?: string; timezone: string }) {
  const { time, date } = eventTimeParts(value, timezone);
  if (!time) {
    return <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>;
  }
  return (
    <>
      <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{time}</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>{date}</Typography>
    </>
  );
}

// What kicked off the upload: the org user who asked for a clip, or — for an
// automatic safety event — the event that fired it.
function TriggeredByCell({ cameraNotification }: { cameraNotification: CameraNotification }) {
  if (cameraNotification.requestedBy) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Iconify icon="solar:user-rounded-bold" width={16} sx={{ color: "text.secondary", flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>{cameraNotification.requestedBy}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Requested</Typography>
        </Box>
      </Stack>
    );
  }
  const visual = eventVisual(cameraNotification.eventCode);
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Iconify icon={visual.icon} width={16} sx={{ color: visual.color, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>{cameraNotification.reason}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>Automatic event</Typography>
      </Box>
    </Stack>
  );
}

// Each safety-event type gets a meaningful icon and a severity color, so the
// lead column carries information (and a calm visual rhythm) instead of the
// mismatched colored blocks + gray camera icons it had before.
const SEVERITY = {
  critical: "#B71D18", // crash / SOS
  high: "#FF5630",     // collision warnings, overspeed
  medium: "#FFAB00",   // harsh driving, driver-monitoring alerts
  info: "#2196F3",     // seatbelt
  neutral: "#637381",  // requested clips, everything else
};

type EventVisual = { icon: string; color: string };

const EVENT_VISUALS: Record<string, EventVisual> = {
  "04": { icon: "mdi:car-emergency", color: SEVERITY.critical },       // crash
  "0A": { icon: "mdi:alarm-light", color: SEVERITY.critical },         // SOS
  "09": { icon: "mdi:alarm-light", color: SEVERITY.critical },         // panic
  A1: { icon: "mdi:car-brake-alert", color: SEVERITY.high },           // forward collision
  A2: { icon: "mdi:walk", color: SEVERITY.high },                      // pedestrian collision
  A3: { icon: "mdi:car-distance-spacing", color: SEVERITY.high },      // headway
  "08": { icon: "mdi:speedometer", color: SEVERITY.high },             // overspeed
  "05": { icon: "mdi:speedometer", color: SEVERITY.medium },           // harsh accel
  "06": { icon: "mdi:car-brake-alert", color: SEVERITY.medium },       // harsh brake
  "07": { icon: "mdi:steering", color: SEVERITY.medium },              // harsh turn
  D1: { icon: "solar:eye-closed-bold", color: SEVERITY.medium },       // eye close
  D2: { icon: "solar:sleeping-square-bold", color: SEVERITY.medium },  // yawning
  D3: { icon: "solar:eye-scan-bold", color: SEVERITY.medium },         // distraction
  D4: { icon: "mdi:smoking", color: SEVERITY.medium },                 // smoking
  D5: { icon: "solar:smartphone-bold", color: SEVERITY.medium },       // phone use
  D6: { icon: "mdi:account-alert", color: SEVERITY.medium },           // driver abnormal
  D7: { icon: "mdi:glasses", color: SEVERITY.medium },                 // IR blocking
  D8: { icon: "mdi:seatbelt", color: SEVERITY.info },                  // seatbelt
  F0: { icon: "solar:videocamera-record-bold", color: SEVERITY.info },  // requested video
  F1: { icon: "solar:videocamera-record-bold", color: SEVERITY.info },
};

function eventVisual(eventCode: string): EventVisual {
  return EVENT_VISUALS[eventCode] || { icon: "solar:videocamera-record-bold", color: SEVERITY.neutral };
}

// A uniform 16:10 lead tile: the real preview when we have one, otherwise a
// soft-tinted well with the event icon. Same size in every state so the first
// column stays a clean, aligned strip.
function EventThumbnail({ cameraNotification }: { cameraNotification: CameraNotification }) {
  const visual = eventVisual(cameraNotification.eventCode);
  const hasVideo = !!cameraNotification.videoUrl || cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_AVAILABLE;

  return (
    <Box
      sx={{
        position: "relative",
        width: 72,
        height: 46,
        borderRadius: 1.5,
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(visual.color, 0.12),
        border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
      }}
    >
      {cameraNotification.previewUrl ? (
        <Box
          component="img"
          src={cameraNotification.previewUrl}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Iconify icon={visual.icon} width={22} sx={{ color: visual.color }} />
      )}

      {hasVideo && (
        <Box
          sx={{
            position: "absolute",
            right: 4,
            bottom: 4,
            width: 16,
            height: 16,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha("#000", 0.55),
          }}
        >
          <Iconify icon="solar:play-bold" width={9} sx={{ color: "common.white" }} />
        </Box>
      )}
    </Box>
  );
}

function VideoStatusChip({ cameraNotification }: { cameraNotification: CameraNotification }) {
  if (cameraNotification.videoUrl || cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_AVAILABLE) {
    return (
      <Chip
        size="small"
        color="success"
        variant="soft"
        icon={<Iconify icon="solar:play-circle-bold" width={15} />}
        label="Video Ready"
      />
    );
  }
  if (cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_QUEUED) {
    return (
      <Tooltip title="Waiting for the camera to come online. The request will be sent when it reconnects.">
        <Chip
          size="small"
          color="warning"
          variant="soft"
          icon={<Iconify icon="solar:clock-circle-bold" width={14} />}
          label="Queued"
        />
      </Tooltip>
    );
  }
  if (cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_PENDING) {
    return (
      <Chip
        size="small"
        color="info"
        variant="soft"
        icon={<CircularProgress size={11} thickness={6} color="info" />}
        label="Uploading"
      />
    );
  }
  if (cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_FAILED) {
    return (
      <Tooltip title={cameraNotification.videoError || "The video could not be retrieved."}>
        <Chip
          size="small"
          color="error"
          variant="soft"
          icon={<Iconify icon="solar:danger-triangle-bold" width={14} />}
          label="Failed"
        />
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      variant="soft"
      icon={<Iconify icon="solar:videocamera-record-bold" width={14} />}
      label="No Video"
      sx={{ color: "text.secondary", bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12) }}
    />
  );
}

type SafetyInboxProps = {
  cameraNotifications?: CameraNotification[],
  timezone: string,
  onCameraNotificationSelected: (a: CameraNotification) => void
}

const SafetyInbox = memo(({ cameraNotifications, timezone, onCameraNotificationSelected }: SafetyInboxProps) => {
  const { sortedList, sortData } = useSort(cameraNotifications, "eventDateTime", "desc");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["location", "reason", "requestedBy", "eldVehicleUnitNumber", "eldDriverFirstName", "eldDriverLastName"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Events" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell />

              <SortableHeader sortKey="reason" sortData={sortData} >
                Event
              </SortableHeader>

              <SortableHeader sortKey="requestedBy" sortData={sortData} >
                Triggered By
              </SortableHeader>

              <SortableHeader sortKey="eventDateTime" sortData={sortData} >
                Event Time
              </SortableHeader>

              <SortableHeader sortKey="createdAt" sortData={sortData} >
                Requested At
              </SortableHeader>

              <SortableHeader sortKey="videoUploadedAt" sortData={sortData} >
                Uploaded At
              </SortableHeader>

              <SortableHeader sortKey="location" sortData={sortData} >
                Location
              </SortableHeader>

              <SortableHeader sortKey="eldVehicleUnitNumber" sortData={sortData} >
                Vehicle
              </SortableHeader>

              <SortableHeader sortKey="videoStatus" sortData={sortData} >
                Video
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((cameraNotification, index) => (
              <TableRow
                key={cameraNotification.id || index}
                hover
                onClick={() => onCameraNotificationSelected(cameraNotification)}
                sx={{ cursor: "pointer" }}
              >
                <TruxTableCell sx={{ pr: 0 }}>
                  <EventThumbnail cameraNotification={cameraNotification} />
                </TruxTableCell>

                <TruxTableCell>
                  <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                    {cameraNotification.reason}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {cameraNotification.durationSeconds}s clip
                  </Typography>
                </TruxTableCell>

                <TruxTableCell>
                  <TriggeredByCell cameraNotification={cameraNotification} />
                </TruxTableCell>

                <TruxTableCell>
                  <TimeCell value={cameraNotification.eventDateTime} timezone={timezone} />
                </TruxTableCell>

                <TruxTableCell>
                  <TimeCell value={cameraNotification.createdAt} timezone={timezone} />
                </TruxTableCell>

                <TruxTableCell>
                  <TimeCell value={cameraNotification.videoUploadedAt} timezone={timezone} />
                </TruxTableCell>

                <TruxTableCell>
                  {cameraNotification.location
                    ? <Typography variant="body2">{cameraNotification.location}</Typography>
                    : <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>}
                </TruxTableCell>

                <TruxTableCell>
                  {cameraNotification.eldVehicleUnitNumber
                    ? <Chip
                        size="small"
                        variant="soft"
                        label={cameraNotification.eldVehicleUnitNumber}
                        sx={{ fontWeight: 600, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12) }}
                      />
                    : <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>}
                </TruxTableCell>

                <TruxTableCell>
                  <VideoStatusChip cameraNotification={cameraNotification} />
                </TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type EventVideoDialogProps = {
  cameraNotification?: CameraNotification,
  timezone: string,
  onClose: () => void,
}

function EventVideoDialog({ cameraNotification, timezone, onClose }: EventVideoDialogProps) {
  const videoOptions = useMemo(
    () => ({
      controls: true,
      autoplay: true,
      muted: true,
      fluid: true,
      sources: [
        {
          src: cameraNotification?.videoUrl,
          type: 'video/mp4',
        },
      ],
    }),
    [cameraNotification?.videoUrl]
  );

  if (!cameraNotification) {
    return null;
  }

  const detail = (label: string, value: string) => !value ? null : (
    <Stack direction="row" gap={0.75} alignItems="baseline">
      <Typography variant="caption" sx={{ color: alpha("#fff", 0.45) }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: alpha("#fff", 0.85), fontWeight: 600 }}>{value}</Typography>
    </Stack>
  );

  const driver = `${cameraNotification.eldDriverFirstName || ""} ${cameraNotification.eldDriverLastName || ""}`.trim();
  const isPending = cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_PENDING;
  const isFailed = cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_FAILED;
  const isQueued = cameraNotification.videoStatus === CAMERA_VIDEO_STATUS_QUEUED;

  // Force-download via the backend, which redirects to a presigned S3 URL with a
  // Content-Disposition attachment. Token in the query since it's a plain browser
  // navigation, not the axios client.
  const downloadUrl = cameraNotification.videoUrl
    ? `${HOST_API}${formatEndpoint(endpoints.cameras.videoDownload, { id: cameraNotification.id })}?token=${encodeURIComponent(localStorage.getItem(STORAGE_KEY) || "")}`
    : undefined;

  const footer = (
    <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={1}>
      {detail("Time", formatEventTime(cameraNotification.eventDateTime, timezone))}
      {detail("Location", cameraNotification.location)}
      {detail("Driver", driver)}
      {detail("Speed", cameraNotification.speed ? `${Math.round(cameraNotification.speed * 0.621371)} mph` : "")}
    </Stack>
  );

  return (
    <CameraMediaDialog
      open
      onClose={onClose}
      title={cameraNotification.reason || "Camera Event"}
      subtitle={cameraNotification.eldVehicleUnitNumber ? `Vehicle ${cameraNotification.eldVehicleUnitNumber}` : undefined}
      downloadUrl={downloadUrl}
      footer={footer}
    >
      {cameraNotification.videoUrl ? (
        <Box sx={{ width: "100%" }}>
          <VideoJS options={videoOptions} />
        </Box>
      ) : isQueued ? (
        <CameraMediaMessage
          icon="solar:clock-circle-bold"
          iconColor="#FFAB00"
          title="Waiting for the camera"
          subtitle="The camera is offline — this request will be sent as soon as it reconnects."
        />
      ) : isPending ? (
        <CameraMediaLoading
          label="Uploading video…"
          sublabel="It will appear here once the camera finishes uploading."
        />
      ) : isFailed ? (
        <CameraMediaMessage
          icon="solar:danger-triangle-bold"
          iconColor="#FF5630"
          title="Video unavailable"
          subtitle={cameraNotification.videoError || "The camera could not upload the video for this event."}
        />
      ) : (
        <CameraMediaMessage
          icon="solar:videocamera-record-bold"
          title="No video attached"
          subtitle="This event has no video."
        />
      )}
    </CameraMediaDialog>
  );
}

type PageModel = {
  cameraNotifications?: CameraNotification[],
  cameras?: CameraWithDevice[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const settings = useSettingsContext();
  const pageFilterData = usePageFilterLoaderData(
      addDaysToDate(today(timezone), -2),
      today(timezone)
    );

  const dataLoader = useCallback(() => ({
    cameras: axios.get(endpoints.cameras.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    cameraNotifications: !pageFilterData.fromDate || !pageFilterData.toDate
      ? []
      : axios.get(formatEndpoint(endpoints.cameraNotifications.findAll, {
                fromDate: pageFilterData.fromDate,
                toDate: pageFilterData.toDate,
              })).then(response => response.data),
  }), [pageFilterData]);
  const {
    data,
    updateData,
    error,
    loading,
    loaded,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [selectedCameraNotification, setSelectedCameraNotification] = useState<CameraNotification>();

  // live updates: new events (and video-arrived updates) push over a websocket
  const notificationsRef = useRef<CameraNotification[] | undefined>(data?.cameraNotifications);
  notificationsRef.current = data?.cameraNotifications;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let shouldConnect = true;

    const connect = () => {
      if (!shouldConnect) {
        return;
      }
      try {
        ws = new WebSocket(`${HOST_API}${endpoints.cameraNotifications.realtime}?token=${encodeURIComponent(localStorage.getItem(STORAGE_KEY) || '')}`);
      } catch (e) {
        reconnectTimer = setTimeout(connect, 2000);
        return;
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type !== 'cameraNotification' || !message.notification) {
            return;
          }
          const incoming = message.notification as CameraNotification;
          const current = notificationsRef.current || [];
          const existing = current.find((line) => line.id === incoming.id);
          const next = existing
            ? current.map((line) => (line.id === incoming.id ? incoming : line))
            : [incoming, ...current];
          // Keep the ref in sync now, not on the next render, so a burst of
          // messages in one tick each build on the prior update instead of
          // overwriting it from the same stale snapshot.
          notificationsRef.current = next;
          updateData("cameraNotifications")(next);
          setSelectedCameraNotification((selected) =>
            selected && selected.id === incoming.id ? incoming : selected
          );
        } catch (e) {
          // not JSON — ignore
        }
      };

      ws.onclose = () => {
        if (shouldConnect) {
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
    };

    connect();

    return () => {
      shouldConnect = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      ws?.close();
    };
  }, [updateData]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.cameras?.length !== 0 &&
          <>
            <PageHeader title="Safety Inbox" />
            <PageFilterLoader pageFilterData={pageFilterData} onRun={() => {
              setShouldRefresh(true);
            }} />
          </>
        }

        {data && data?.cameras?.length === 0 && <NoCamerasFound />}

        {data && data?.cameras?.length !== 0 && <>
          {loaded && data && data.cameraNotifications?.length === 0 ? <NoneFoundReport /> : <SafetyInbox
            cameraNotifications={data.cameraNotifications}
            timezone={timezone}
            onCameraNotificationSelected={setSelectedCameraNotification}
          />}
        </>}

        {selectedCameraNotification && <EventVideoDialog
          cameraNotification={selectedCameraNotification}
          timezone={timezone}
          onClose={() => setSelectedCameraNotification(undefined)}
        />}
      </DisplayPageData>
    </Container>
  );
}
